// Tracks undo/redo snapshots and applies history transitions for sheet state.
import { useCallback, useRef, useState } from "react";
import type React from "react";
import type { SheetAccountEntry } from "../../../types/sheetDocument";
import type { TAccountCatalogItem } from "../types";

export interface SheetSnapshot {
  tAccounts: SheetAccountEntry[];
  taccountCatalog: TAccountCatalogItem[];
  uberCommentsByLetter: Record<string, string>;
  forceTwoDecimalDisplay: boolean;
  documentTitle: string;
  nextAutoLetterIndex: number;
  nextEntrySequence: number;
}

export interface SheetHistorySnapshot {
  undoStack: SheetSnapshot[];
  redoStack: SheetSnapshot[];
}

interface UseSheetHistoryParams {
  tAccountsRef: React.MutableRefObject<SheetAccountEntry[]>;
  uberCommentsByLetterRef: React.MutableRefObject<Record<string, string>>;
  forceTwoDecimalDisplayRef: React.MutableRefObject<boolean>;
  documentTitleRef: React.MutableRefObject<string>;
  nextAutoLetterIndexRef: React.MutableRefObject<number>;
  nextEntrySequenceRef: React.MutableRefObject<number>;
  taccountCatalogRef: React.MutableRefObject<TAccountCatalogItem[]>;
  setTAccounts: React.Dispatch<React.SetStateAction<SheetAccountEntry[]>>;
  setUberCommentsByLetter: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setForceTwoDecimalDisplay: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentTitle: React.Dispatch<React.SetStateAction<string>>;
  setTAccountCatalog: React.Dispatch<React.SetStateAction<TAccountCatalogItem[]>>;
  onBeforeApplySnapshot?: (snapshot: SheetSnapshot) => Promise<boolean>;
  onUndoRedoApplied: () => void;
}

function cloneTAccounts(accounts: SheetAccountEntry[]): SheetAccountEntry[] {
  return accounts.map((account) => ({
    ...account,
    entries: account.entries.map((entry) => ({ ...entry })),
  }));
}

export default function useSheetHistory({
  tAccountsRef,
  uberCommentsByLetterRef,
  forceTwoDecimalDisplayRef,
  documentTitleRef,
  nextAutoLetterIndexRef,
  nextEntrySequenceRef,
  taccountCatalogRef,
  setTAccounts,
  setUberCommentsByLetter,
  setForceTwoDecimalDisplay,
  setDocumentTitle,
  setTAccountCatalog,
  onBeforeApplySnapshot,
  onUndoRedoApplied,
}: UseSheetHistoryParams) {
  const [undoStack, setUndoStack] = useState<SheetSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<SheetSnapshot[]>([]);
  const undoStackRef = useRef<SheetSnapshot[]>([]);
  const redoStackRef = useRef<SheetSnapshot[]>([]);
  const transitionPendingRef = useRef(false);
  const [isTransitionPending, setIsTransitionPending] = useState(false);
  const generationRef = useRef(0);

  const beginTransition = useCallback((): boolean => {
    if (transitionPendingRef.current) return false;
    transitionPendingRef.current = true;
    setIsTransitionPending(true);
    return true;
  }, []);

  const endTransition = useCallback((): void => {
    transitionPendingRef.current = false;
    setIsTransitionPending(false);
  }, []);

  const buildSnapshot = useCallback((): SheetSnapshot => ({
    tAccounts: cloneTAccounts(tAccountsRef.current),
    taccountCatalog: taccountCatalogRef.current.map((item) => ({ ...item })),
    uberCommentsByLetter: { ...uberCommentsByLetterRef.current },
    forceTwoDecimalDisplay: forceTwoDecimalDisplayRef.current,
    documentTitle: documentTitleRef.current,
    nextAutoLetterIndex: nextAutoLetterIndexRef.current,
    nextEntrySequence: nextEntrySequenceRef.current,
  }), [
    documentTitleRef,
    forceTwoDecimalDisplayRef,
    nextAutoLetterIndexRef,
    nextEntrySequenceRef,
    tAccountsRef,
    taccountCatalogRef,
    uberCommentsByLetterRef,
  ]);

  const applySnapshot = useCallback((snapshot: SheetSnapshot): void => {
    tAccountsRef.current = cloneTAccounts(snapshot.tAccounts);
    uberCommentsByLetterRef.current = { ...snapshot.uberCommentsByLetter };
    forceTwoDecimalDisplayRef.current = snapshot.forceTwoDecimalDisplay;
    documentTitleRef.current = snapshot.documentTitle;
    setTAccounts(tAccountsRef.current);
    setUberCommentsByLetter(uberCommentsByLetterRef.current);
    setForceTwoDecimalDisplay(snapshot.forceTwoDecimalDisplay);
    setDocumentTitle(snapshot.documentTitle);
    taccountCatalogRef.current = snapshot.taccountCatalog.map((item) => ({ ...item }));
    setTAccountCatalog(taccountCatalogRef.current);
    nextAutoLetterIndexRef.current = snapshot.nextAutoLetterIndex;
    nextEntrySequenceRef.current = snapshot.nextEntrySequence;
  }, [
    tAccountsRef,
    uberCommentsByLetterRef,
    forceTwoDecimalDisplayRef,
    documentTitleRef,
    nextAutoLetterIndexRef,
    nextEntrySequenceRef,
    setDocumentTitle,
    setForceTwoDecimalDisplay,
    setTAccounts,
    setTAccountCatalog,
    setUberCommentsByLetter,
    taccountCatalogRef,
  ]);

  const pushSnapshot = useCallback((snapshot: SheetSnapshot): void => {
    undoStackRef.current = [...undoStackRef.current, snapshot];
    redoStackRef.current = [];
    setUndoStack(undoStackRef.current);
    setRedoStack([]);
  }, []);

  const clearHistory = useCallback((): void => {
    generationRef.current += 1;
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  const captureHistory = useCallback((): SheetHistorySnapshot => ({
    undoStack: [...undoStackRef.current],
    redoStack: [...redoStackRef.current],
  }), []);

  const applyHistoryTransition = useCallback(async (direction: "undo" | "redo"): Promise<boolean> => {
    const sourceStack = direction === "undo" ? undoStackRef.current : redoStackRef.current;
    if (sourceStack.length === 0) return false;

    if (!beginTransition()) return false;
    const generation = generationRef.current;
    try {
      const snapshotToRestore = sourceStack[sourceStack.length - 1];
      if (onBeforeApplySnapshot && !(await onBeforeApplySnapshot(snapshotToRestore))) return false;
      if (generation !== generationRef.current) return false;

      const currentSnapshot = buildSnapshot();
      if (direction === "undo") {
        undoStackRef.current = sourceStack.slice(0, -1);
        redoStackRef.current = [...redoStackRef.current, currentSnapshot];
      } else {
        redoStackRef.current = sourceStack.slice(0, -1);
        undoStackRef.current = [...undoStackRef.current, currentSnapshot];
      }
      setUndoStack(undoStackRef.current);
      setRedoStack(redoStackRef.current);
      applySnapshot(snapshotToRestore);
      onUndoRedoApplied();
      return true;
    } finally {
      endTransition();
    }
  }, [applySnapshot, beginTransition, buildSnapshot, endTransition, onBeforeApplySnapshot, onUndoRedoApplied]);

  const undo = useCallback(() => applyHistoryTransition("undo"), [applyHistoryTransition]);
  const redo = useCallback(() => applyHistoryTransition("redo"), [applyHistoryTransition]);

  const handleUndo = useCallback((): void => {
    void undo().catch(() => undefined);
  }, [undo]);
  const handleRedo = useCallback((): void => {
    void redo().catch(() => undefined);
  }, [redo]);

  const restoreSnapshot = useCallback(async (
    snapshot: SheetSnapshot,
    history: SheetHistorySnapshot,
  ): Promise<boolean> => {
    if (!beginTransition()) return false;
    const generation = generationRef.current;
    try {
      if (onBeforeApplySnapshot && !(await onBeforeApplySnapshot(snapshot))) return false;
      if (generation !== generationRef.current) return false;

      undoStackRef.current = [...history.undoStack];
      redoStackRef.current = [...history.redoStack];
      setUndoStack(undoStackRef.current);
      setRedoStack(redoStackRef.current);
      applySnapshot(snapshot);
      onUndoRedoApplied();
      return true;
    } finally {
      endTransition();
    }
  }, [applySnapshot, beginTransition, endTransition, onBeforeApplySnapshot, onUndoRedoApplied]);

  return {
    beginTransition,
    endTransition,
    isTransitionPending,
    undoStack,
    redoStack,
    buildSnapshot,
    pushSnapshot,
    clearHistory,
    captureHistory,
    undo,
    redo,
    restoreSnapshot,
    handleUndo,
    handleRedo,
  };
}
